import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ENDPOINTS from "../../config/apiUrls";
import axiosInstance from "../../config/axiosInstance";
import _ from 'lodash';
import { formatTimestamp, getOwnerEmail } from "../../helpers/utils";
import qs from 'qs';
import Swal from "sweetalert2";
import { HUMAN_TIMEZONES } from "../../constants";
import { convertTimeToUtc } from "../../helpers/date-time";
import { toast } from "react-toastify";
import moment from "moment";
import { purgeCloudflareCache } from "../../helpers/cloudflareCache";


export const getContestsList = createAsyncThunk(
  'contest/getContestsList',
  async ({ page = 1, limit = 10 }, { getState }) => {
    const { auth } = getState();
    const roomId = auth?.user?.attributes?.stationChatId;

    try {
      const response = await axiosInstance.get(ENDPOINTS.CONTEST.LIST, {
        params: {
          'filter[ownerId]': roomId,
          'page[number]': page,
          'page[size]': limit
        }
      });

      const contests = response.data.data;

      await Promise.all(contests.map(async (contest) => {
        if (!contest.attributes.hasQuestions || !contest.relations.poll.data.id) {
          if (!contest.attributes.hasQuestions && contest.relations.users.data.length) {
            return {
              ...contest,
              relations: {
                ...contest.relations,
                users: {
                  data: contest.relations.users.data
                }
              }
            };
          }
          return contest;
        }

        // Handle contests with questions
        const pollId = contest.relations.poll.data.id;
        const answersResponse = await axiosInstance.get(`${ENDPOINTS.POLLS.ANSWERS}?id=${pollId}`);

        const answersData = answersResponse.data.meta.answers.map(answer => ({
          ...answer,
          users: answersResponse.data.meta.users
            .filter(user => user.answerId === answer.id)
            .map(user => user.userId)
        }));

        // Update contest data
        contest.relations.poll.data.relations.answers.data =
          contest.relations.poll.data.relations.answers.data.map(relation => {
            const answer = answersData.find(a => a.id === relation.id);
            if (!answer) return relation;

            return {
              ...relation,
              attributes: {
                ...relation.attributes,
                votesCount: answer.votesCount,
                userParticipated: answer.users || []
              }
            };
          });

        // Update total votes count
        contest.attributes.votesCount = answersData.reduce(
          (sum, answer) => sum + answer.votesCount, 0
        );

        return contest;
      }));

      return {
        data: contests,
        meta: response.data.meta
      };
    } catch (error) {
      console.error('Failed to load contest history:', error);
      throw error;
    }
  }
);

export const exportContestParticipants = createAsyncThunk(
  'contests/exportContestParticipants',
  async ({ id, pollId }, { getState }) => {
    try {
      const { auth, contest } = getState();
      const roomId = auth?.user?.attributes?.stationChatId;
      let userIds = [];


      if (pollId) {
        const { data } = await axiosInstance.get(`${ENDPOINTS.POLLS.USERS_LIST}?id=${pollId}`);

        userIds = data.data.map(user => user.id);
        if (userIds.length === 0) return [];
      } else {
        const selectedContest = contest.contests.find((contest) => contest.id === id);
        userIds = selectedContest?.relations.users.data;
      }

      const { data: userMetaData } = await axiosInstance.get(ENDPOINTS.USERS.UPDATE_PROFILE, {
        params: { id: userIds },
        paramsSerializer: params => qs.stringify(params, { indices: false }),
      });
      const { data: chatParticipants } = await axiosInstance.post(ENDPOINTS.CHAT.PARTICIPANT_BY_ID, {
        roomId,
        id: userIds,
      });

      return (Array.isArray(userMetaData?.data) ? userMetaData?.data : [userMetaData?.data])?.map(user => {
        if (!user) return null; // Handle cases where userMetaData.data is null/undefined

        const match = chatParticipants?.find(firstUser => firstUser.id === user.id);
        return {
          name: match?.user?.name || user.attributes.firstName + ' ' + user.attributes.lastName,
          userId: user.id || '',
          email: match?.user?.email || user.attributes.email || 'N/A',
          DOB: match?.user?.birthday != null ? moment(match?.user?.birthday).format("MM/DD/YYYY") : "N/A",
          Gender: match?.user?.gender ?? "N/A",
          avatar: match?.user?.avatar || user.attributes.avatar || null,
          zipcode: match?.user?.zipcode || user.attributes.zipcode || 'N/A',
          joinedAt: formatTimestamp(user.attributes.created),
          activity: match?.user?.lastActivityAt ? formatTimestamp(match.user.lastActivityAt) : 'N/A',
          status: match?.user?.bannedAt ? 'Blocked' : 'Active',
          role: match?.user?.roles?.length ? match.user.roles.join(' ') : 'Participant',
          extraInfo: user.attributes

        };
      }).filter(Boolean);
    } catch (error) {
      console.log(error.message, 'errorrrrrrrrrrrrrrr')
    }
  }
);

export const exportContestWinners = createAsyncThunk(
  'contests/exportContestParticipants',
  async ({ id }, { getState }) => {
    try {
      const { auth, contest } = getState();
      const roomId = auth?.user?.attributes?.stationChatId;
      let userIds = [];
      const selectedContest = contest.contests.find((contest) => contest.id === id);
      userIds = selectedContest?.attributes.meta.winners;

      const { data: userMetaData } = await axiosInstance.get(ENDPOINTS.USERS.UPDATE_PROFILE, {
        params: { id: userIds },
        paramsSerializer: params => qs.stringify(params, { indices: false }),
      });
      const { data: chatParticipants } = await axiosInstance.post(ENDPOINTS.CHAT.PARTICIPANT_BY_ID, {
        roomId,
        id: userIds,
      });

      return (Array.isArray(userMetaData?.data) ? userMetaData?.data : [userMetaData?.data])?.map(user => {
        if (!user) return null; // Handle cases where userMetaData.data is null/undefined

        const match = chatParticipants?.find(firstUser => firstUser.id === user.id);
        return {
          name: match?.user?.name || user.attributes.firstName + ' ' + user.attributes.lastName,
          userId: user.id || '',
          email: match?.user?.email || user.attributes.email || 'N/A',
          DOB: match?.user?.birthday != null ? moment(match?.user?.birthday).format("MM/DD/YYYY") : "N/A",
          Gender: match?.user?.gender ?? "N/A",
          avatar: match?.user?.avatar || user.attributes.avatar || null,
          zipcode: match?.user?.zipcode || user.attributes.zipcode || 'N/A',
          joinedAt: formatTimestamp(user.attributes.created),
          activity: match?.user?.lastActivityAt ? formatTimestamp(match.user.lastActivityAt) : 'N/A',
          status: match?.user?.bannedAt ? 'Blocked' : 'Active',
          role: match?.user?.roles?.length ? match.user.roles.join(' ') : 'Participant',
          extraInfo: user.attributes

        };
      }).filter(Boolean);
    } catch (error) {
      console.log(error.message, 'errorrrrrrrrrrrrrrr')
    }
  }
);

export const deleteContest = createAsyncThunk(
  "contests/deleteContest",
  async ({ id }, { getState, rejectWithValue }) => {
    try {
      await axiosInstance.post(ENDPOINTS.CONTEST.DELETE, { id });

      Swal.fire("Deleted!", "Contest has been Successfully deleted.", "success");
      const { stationGroup } = getState().auth.user.attributes;
      if (stationGroup) {
          await purgeCloudflareCache(stationGroup);
      }
      return { id };
    } catch (error) {
      console.log(error.message, 'error')
      return rejectWithValue(error.response?.data || "Error loading messages");
    }
  }
);

export const stopContest = createAsyncThunk(
  "contests/stopContest",
  async ({ id }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const ownerEmail = getOwnerEmail(auth.user);

      let contest = await axiosInstance.post(ENDPOINTS.CONTEST.STOP, { id, ownerEmail: ownerEmail });
      const { stationGroup } = getState().auth.user.attributes;
      if (stationGroup) {
          await purgeCloudflareCache(stationGroup);
      }
      return contest.data.data;
    } catch (error) {
      console.log(error.message, 'error')
      return rejectWithValue(error.response?.data || "Error loading messages");
    }
  }
);

export const archiveContest = createAsyncThunk(
  "contests/archiveContest",
  async ({ id }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const ownerEmail = getOwnerEmail(auth.user);

      await axiosInstance.post(ENDPOINTS.CONTEST.ARCHIVE, { id });
      const { stationGroup } = getState().auth.user.attributes;
      if (stationGroup) {
          await purgeCloudflareCache(stationGroup);
      }
      return { id, newState: 4 };
    } catch (error) {
      console.log(error.message, 'error')
      return rejectWithValue(error.response?.data || "Error loading messages");
    }
  }
);

export const startContest = createAsyncThunk(
  "contests/startContest",
  async ({ id }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const ownerEmail = getOwnerEmail(auth.user);

      let contest = await axiosInstance.post(ENDPOINTS.CONTEST.START, { id, ownerEmail: ownerEmail });
      const { stationGroup } = getState().auth.user.attributes;
      if (stationGroup) {
          await purgeCloudflareCache(stationGroup);
      }
      return contest.data.data;
    } catch (error) {
      console.log(error.message, 'error')
      return rejectWithValue(error.response?.data || "Error loading messages");
    }
  }
);

export const endAndGenerateWinner = createAsyncThunk(
  "contests/endAndGenerateWinner",
  async ({ id, answerId }, { getState, rejectWithValue }) => {
    try {
      let payload = { id };
      if (answerId) {
        payload.answerId = answerId;
      }
      let result = await axiosInstance.post(ENDPOINTS.CONTEST.END_CONTEST_GENERATE_WINNER, payload);
      const { stationGroup } = getState().auth.user.attributes;
      if (stationGroup) {
          await purgeCloudflareCache(stationGroup);
      }
      return result.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error loading messages");
    }
  }
);

export const createContest = createAsyncThunk(
  'contests/create',
  async ({ data }, { getState }) => {
    try {
      const preparedData = {
        ...data,
        answers: data.answers.filter(ans => typeof ans === 'string' && ans.trim() !== '')
      };

      const { ownerEmail, stationChatId } = getState().auth.user.attributes;

      const scheduledStartTime = preparedData.startDate
        ? convertTimeToUtc(preparedData.startDate, preparedData.startTime, HUMAN_TIMEZONES[preparedData.tz])
        : new Date();

      const scheduledEndTime = preparedData.endDate
        ? convertTimeToUtc(preparedData.endDate, preparedData.endTime, HUMAN_TIMEZONES[preparedData.tz])
        : new Date();

      const contestResponse = await axiosInstance.post(ENDPOINTS.CONTEST.CREATE, {
        prize: preparedData.prize,
        ownerId: stationChatId,
        hasQuestions: preparedData.hasQuestions,
        onlyFollowers: preparedData.followersOnly,
        nWinners: parseInt(preparedData.nWinners, 10),
        scheduledStartTime,
        scheduledEndTime,
        tz: preparedData.tz || null,
        isScheduled: preparedData.startDate ? 1 : 0,
        displayData: JSON.stringify({
          startTime: preparedData.startTime || '',
          endTime: preparedData.endTime || '',
          startDate: preparedData.startDate || '',
          endDate: preparedData.endDate || '',
        }),
        ownerEmail: preparedData.startDate ? ownerEmail : null,
        image: preparedData.image ? preparedData.image : "",
        description: preparedData.description ? preparedData.description : "",
        meta: {
          rules: preparedData.rules,
        },
      });

      const contestId = contestResponse.data?.data?.id;

      if (preparedData.hasQuestions) {
        const pollResponse = await axiosInstance.post(ENDPOINTS.POLLS.CREATE, {
          contestId,
          title: preparedData.title,
          ownerId: stationChatId,
          tz: null,
          scheduledStartTime: new Date(),
          scheduledEndTime: new Date(),
          isScheduled: 0,
          minUserAnswersCount: 1,
          maxUserAnswersCount: 1,
        });

        const pollId = pollResponse.data?.data?.id;

        await Promise.all(
          preparedData.answers.map((answer, idx) =>
            axiosInstance.post(ENDPOINTS.POLLS.ANSWER_CREATE, {
              pollId,
              title: answer,
              position: idx,
            })
          )
        );

        const fullContest = await axiosInstance.get(`${ENDPOINTS.CONTEST.GET_BY_ID}?id=${contestId}`);
        
        // Purge Cloudflare cache for users/list URL
        const { stationGroup } = getState().auth.user.attributes;
        if (stationGroup) {
            await purgeCloudflareCache(stationGroup);
        }
        
        return fullContest.data.data;
      }

      // Purge Cloudflare cache for users/list URL
      const { stationGroup } = getState().auth.user.attributes;
      if (stationGroup) {
          await purgeCloudflareCache(stationGroup);
      }

      return contestResponse.data.data;
    } catch (error) {
      console.log('error.message', error.message)
    }
  }
);

export const updateContest = createAsyncThunk(
  'contests/update',
  async ({ data }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      const ownerEmail = getOwnerEmail(auth.user);
      console.log(ownerEmail,'ownerEmail')
      let existingContest = getState().contest.selectedContest;
      let updatedContestState = {};
      const updateFields = {
        ownerEmail: ownerEmail,
      };

      if (
        data.prize !== existingContest?.attributes?.prize ||
        data.nWinners !== existingContest?.attributes?.nWinners ||
        data.rules !== existingContest?.attributes?.meta.rules
      ) {
        updateFields.prize = data.prize;
        updateFields.nWinners = parseInt(data.nWinners, 10);
        
      }
      updateFields.meta = {
        rules: data.rules || "",
      };
      updateFields.ownerEmail = ownerEmail;
      if (existingContest?.attributes?.isScheduled) {
        
        const displayData = JSON.parse(existingContest?.attributes?.displayData || '{}');

        if (
          data.startDate !== displayData.startDate ||
          data.startTime !== displayData.startTime
        ) {
          updateFields.scheduledStartTime = data.startDate
            ? convertTimeToUtc(data.startDate, data.startTime, HUMAN_TIMEZONES[data.tz])
            : new Date();
        }

        if (
          data.endDate !== displayData.endDate ||
          data.endTime !== displayData.endTime
        ) {
          updateFields.scheduledEndTime = data.endDate
            ? convertTimeToUtc(data.endDate, data.endTime, HUMAN_TIMEZONES[data.tz])
            : new Date();
        }
      }

      if (data.tz !== existingContest?.attributes?.tz) {
        updateFields.tz = data.tz || null;
      }

      const newDisplayData = JSON.stringify({
        startTime: data.startTime || '',
        endTime: data.endTime || '',
        startDate: data.startDate || '',
        endDate: data.endDate || '',
      });

      if (newDisplayData !== existingContest?.attributes?.displayData) {
        updateFields.displayData = newDisplayData;
      }

      if (data.image !== existingContest.image) {
        updateFields.image = data.image;
      }

      if (data.description !== existingContest.description) {
        updateFields.description = data.description;
      }

      if (Object.keys(updateFields).length > 0) {
        let resp = await axiosInstance.post(ENDPOINTS.CONTEST.UPDATE, {
          id: existingContest.id,
          ...updateFields,
        });
        updatedContestState = resp.data.data;
      }

      if (data.hasQuestions) {
        updateFields.answers = data.answers.map((answer, idx) => ({
          answer,
          position: idx,
        }));

        const existingPoll = existingContest.relations.poll.data;
        const existingAnswers = existingContest.relations.poll.data.relations.answers.data;

        // Title update
        if (data.title !== existingPoll.attributes.title) {
          await axiosInstance.post(ENDPOINTS.POLLS.UPDATE, {
            id: existingPoll.id,
            title: data.title,
          });
        }

        // Remove deleted answers
        const answersToDelete = existingAnswers
          .filter(answer => !updateFields.answers.some(it => it.answer?.id === answer.id));

        for (const answer of answersToDelete) {
          await axiosInstance.post(ENDPOINTS.POLLS.ANSWER_DELETE, { id: answer.id });
        }

        // Update or Create answers
        for (const answer of updateFields.answers) {
          const existing = existingAnswers.find(it => it.id === answer.answer.id);

          if (!answer.answer.id) {
            await axiosInstance.post(ENDPOINTS.POLLS.ANSWER_CREATE, {
              pollId: existingPoll.id,
              title: answer.answer.title,
              position: answer.position,
            });
          } else if (!existing || existing.attributes.title !== answer.answer.title || existing.attributes.position !== answer.position) {
            await axiosInstance.post(ENDPOINTS.POLLS.ANSWER_UPDATE, {
              id: existing.id,
              title: answer.answer.title,
              position: answer.position
            });
          }
        }

        let { data: answersResponse } = await axiosInstance.get(`${ENDPOINTS.POLLS.ANSWERS}?id=${existingPoll.id}`);

        updatedContestState.relations.poll.data.relations.answers.data = answersResponse.data;

        // Purge Cloudflare cache for users/list URL
        const { stationGroup } = getState().auth.user.attributes;
        if (stationGroup) {
            await purgeCloudflareCache(stationGroup);
        }

        return updatedContestState;
      }

      // Purge Cloudflare cache for users/list URL (even if no questions were updated)
      const { stationGroup } = getState().auth.user.attributes;
      if (stationGroup) {
          await purgeCloudflareCache(stationGroup);
      }

      return updatedContestState;

    } catch (error) {
      toast(error.message || 'Something went wrong', {
        position: "bottom-right",
        autoClose: '20000'
      })
      return rejectWithValue(error);
    }

  }
);

const contestSlice = createSlice({
  name: "contest",
  initialState: {
    contests: [],
    filteredContests: [],
    activeContest: null,
    isLoading: false,
    isSearching: false,
    totalRecordsReach: false,
    meta: {},
    isModelOpen: false,
    isWinnerModelOpen: false,
    contestId: null,
    selectedContest: null,
    pollId: null,
    isArchiving: false,
    isUpdating: false,
    isOptionalModelOpen: false,
    optonalInfo: null
  },
  reducers: {
    openContestParticipantModel: (state, action) => {
      state.isModelOpen = true;
      state.contestId = action.payload.id;
      if (action.payload.id) {
        state.selectedContest = state.contests.find((contest) => contest.id == action.payload.id);

      }
      state.pollId = action.payload.pollId;

    },
    openContestWinnerModel: (state, action) => {
      state.isWinnerModelOpen = true;
      state.contestId = action.payload.id;
      if (action.payload.id) {
        state.selectedContest = state.contests.find((contest) => contest.id == action.payload.id);

      }
      state.pollId = action.payload.pollId;

    },
    closeContestParticipantModel: (state) => {
      state.isModelOpen = false;
      state.isWinnerModelOpen = false;
      state.contestId = null;

    },
    openOptionalInfoModel: (state, action) => {
      state.isOptionalModelOpen = true;
      state.optonalInfo = action.payload;

    },
    closeOptionalInfoModel: (state) => {
      state.isOptionalModelOpen = false;
      state.optonalInfo = null;


    },
    getContestById: (state, action) => {
      state.selectedContest = state.contests.find((contest) => contest.id == action.payload.id);
    },
    removeSelectedContest: (state) => {
      state.selectedContest = null
    },
    updateSearchingAction: (state, action) => {
      state.isSearching = action.payload;

    },
    filterContests: (state, action) => {
      const searchQuery = action.payload.toLowerCase();
      state.filteredContests = state.contests.filter(
        (contest) => contest?.attributes?.prize?.toLowerCase().includes(searchQuery)
      );
    },
    resetContestState: (state) => {
      state.contests = [];
      state.filteredContests = [];
      state.activeContest = null;
      state.isLoading = false;
      state.isSearching = false;
      state.totalRecordsReach = false;
      state.meta = {};
      state.isModelOpen = false;
      state.isWinnerModelOpen = false;
      state.contestId = null;
      state.selectedContest = null;
      state.pollId = null;
      state.isArchiving = false;
      state.isUpdating = false;
      state.isOptionalModelOpen = false;
      state.optonalInfo = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getContestsList.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getContestsList.fulfilled, (state, action) => {
        state.isLoading = false;
        const filteredMessages = action.payload.data ?? [];
        const page = action.meta?.arg?.page ?? 1;

        if (page === 1) {
          state.contests = filteredMessages;
          state.filteredContests = filteredMessages;
          state.totalRecordsReach = false;
        } else {
          state.contests = [...state.contests, ...filteredMessages];
          state.filteredContests = [...state.filteredContests, ...filteredMessages];
        }

        state.meta = action?.payload?.meta ?? state.meta;
        if (state.meta?.rowCount != null && state.contests.length >= state.meta.rowCount) {
          state.totalRecordsReach = true;
        }
        state.activeContest =
          state.contests.find(c => c.attributes.state === 1) ||
          state.contests.find(c => c.attributes.state === 2);
      })
      .addCase(startContest.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(startContest.fulfilled, (state, action) => {
        let contestData = action.payload;
        state.contests = state.contests.map(contest =>
          contest.id === contestData.id
            ? contestData
            : contest
        );
        state.filteredContests = state.filteredContests.map(contest =>
          contest.id === contestData.id
            ? contestData
            : contest
        );
        state.activeContest =
          state.contests.find(c => c.attributes.state === 1) ||
          state.contests.find(c => c.attributes.state === 2);
        state.isUpdating = false;

      })
      .addCase(stopContest.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(stopContest.fulfilled, (state, action) => {
        let contestData = action.payload;
        state.contests = state.contests.map(contest =>
          contest.id === contestData.id
            ? contestData
            : contest
        );
        state.filteredContests = state.filteredContests.map(contest =>
          contest.id === contestData.id
            ? contestData
            : contest
        );
        state.activeContest =
          state.contests.find(c => c.attributes.state === 1) ||
          state.contests.find(c => c.attributes.state === 2);

        state.isUpdating = false;
      })
      .addCase(deleteContest.fulfilled, (state, action) => {
        let { id } = action.payload;
        state.contests = state.contests.filter((contest) => contest.id !== id);
        state.filteredContests = state.filteredContests.filter((contest) => contest.id !== id);

        state.activeContest = state.contests.find((contest) => contest.attributes.state === 1 || contest.attributes.state === 2);
      })
      .addCase(archiveContest.fulfilled, (state, action) => {
        let { id, newState } = action.payload;

        state.contests = state.contests.map(contest =>
          contest.id === id
            ? { ...contest, attributes: { ...contest.attributes, state: newState } }
            : contest
        );
        state.filteredContests = state.filteredContests.map(contest =>
          contest.id === id
            ? { ...contest, attributes: { ...contest.attributes, state: newState } }
            : contest
        );
        state.activeContest =
          state.contests.find(c => c.attributes.state === 1) ||
          state.contests.find(c => c.attributes.state === 2);

        state.isUpdating = false;
      })
      .addCase(updateContest.fulfilled, (state, action) => {
        let updateContestState = action.payload;
        state.contests = state.contests.map(contest =>
          contest.id === updateContestState.id
            ? updateContestState
            : contest
        );
        state.filteredContests = state.filteredContests.map(contest =>
          contest.id === updateContestState.id
            ? updateContestState
            : contest
        );
        state.activeContest =
          state.contests.find(c => c.attributes.state === 1) ||
          state.contests.find(c => c.attributes.state === 2);
        state.isArchiving = false;
      })
      .addCase(updateContest.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(endAndGenerateWinner.pending, (state) => {
        state.error = null;
      })
      .addCase(endAndGenerateWinner.fulfilled, (state, action) => {
        const payload = action.payload;
        const id = payload.id;

        state.contests = state.contests.map(contest =>
          contest.id === id ? payload : contest
        );
        state.filteredContests = state.filteredContests.map(contest =>
          contest.id === id ? payload : contest
        );
      })
      .addCase(createContest.fulfilled, (state, action) => {
        console.log(action.payload, 'action.payload')
        const data = action.payload;
        state.contests = [data, ...state.contests];
        state.filteredContests = [data, ...state.filteredContests];

        state.activeContest = data.attributes?.state === 1 || data.attributes?.state === 2
          ? data
          : state.contests.find(poll => poll.attributes.state === 1 || poll.attributes.state === 2);

        state.isArchiving = false;
      });


  },
});

export const { openContestParticipantModel, openContestWinnerModel, closeContestParticipantModel, getContestById, removeSelectedContest, filterContests, updateSearchingAction, openOptionalInfoModel, closeOptionalInfoModel, resetContestState } = contestSlice.actions;


export default contestSlice.reducer;